import React, { ReactElement, useContext } from 'react';

import { ProviderFactory } from '@atlaskit/editor-common/provider-factory';

import {
  ConversationsMentionContext,
  FabricUser,
} from 'src/components/bbc-atlaskit-editor';
import { createContextIdentifierProvider } from 'src/components/bbc-atlaskit-editor/src/providers/context-identifier-provider';
import { ConversationProvider } from 'src/components/conversation-provider';
import {
  FabricComment,
  ProviderConfig,
} from 'src/components/conversation-provider/types';
import { SendAnalyticsEvent } from 'src/components/conversation/src/components/types';
import { providerFactory } from 'src/components/editor/provider-factory';
import { createMentionProvider } from 'src/components/editor/providers/mention-provider';
import { buildProfilecardProvider } from 'src/components/editor/providers/profilecard-provider';
import { MentionsBuildArgs } from 'src/components/editor/providers/recommendations-mentions-provider';
import { EditorSource } from 'src/components/editor/with-analytics';
import { PullRequest, PullRequestState } from 'src/components/types';

export type NewConversationClickEvent = {
  file: string;
  lineTo?: number;
  lineFrom?: number;
};

export type CommentCreateEvent = {
  commentId: string;
  role?: 'REVIEWER' | 'WATCHER' | 'AUTHOR';
  pullRequestLocalId?: number | null;
  repoUuid?: string | null;
  pullRequestStatus?: PullRequestState;
  pending?: boolean;
};

export type CommentExpandCollapseEvent = {
  action: 'expand' | 'collapse';
  resolved?: boolean;
  prStatus?: PullRequestState;
  prId?: number;
  paid?: boolean;
  repoId?: string;
  role?: string;
};

export interface ConversationEventHandlers {
  onNewConversationClick: (event: NewConversationClickEvent) => void;
  onCommentCreate: (event: CommentCreateEvent) => void;
  onCommentExpandCollapse: (event: CommentExpandCollapseEvent) => void;
}

export interface ConversationsContextInterface {
  conversationProvider: ConversationProvider;
  additionalConversationProviders: ProviderFactory;
  renderAdditionalActions: boolean;
  eventHandlers?: ConversationEventHandlers;
  supportsCommentResolution?: boolean;
  user?: FabricUser;
  currentUserUuid?: string;
  pullRequest?: PullRequest;
  workspacePaid: boolean;
  repoId?: string;
  highlightedCommentId?: number;
  highlightCommentThread?: boolean;
  baseRevHash?: string;
  // used for FabricAnalytics
  editorSource: EditorSource;
  activityFeedVersion?: string;
  renderBetweenCommentAndActions?: (
    comment: FabricComment,
    sendAnalyticsEvent: SendAnalyticsEvent,
    disableRender?: boolean,
    collapseComment?: () => void
  ) => JSX.Element;
  additionalAnalyticsAttributes?: (
    comment: FabricComment,
    action: RenderAnalyticsAttributeAction
  ) => object;
}

export const ConversationsContext = React.createContext<
  ConversationsContextInterface | undefined
>(undefined);

export const useConversationsContext = (): ConversationsContextInterface => {
  const context = useContext(ConversationsContext);
  if (context === undefined) {
    throw new Error(
      '`useConversationsContext` must be used within a `ConversationsContext.Provider`'
    );
  }
  return context;
};

// TODO - We would like to eventually refactor our diff and conversation
// logic so that the we don't need this Optional wrapper. Ideally we should only
// call useConversationsContext and only call it inside the context of a ConversationsContext
// If you search for "useOptionalConversationsContext" you'll see that we use this in a generic
// Diff that also gets loaded on the branch page which does not have ConversationsContext.
export const useOptionalConversationsContext = ():
  | ConversationsContextInterface
  | undefined => {
  return useContext(ConversationsContext);
};

type RenderProps = {
  children: (
    context: ConversationsContextInterface | undefined
  ) => ReactElement | null;
};

/* eslint @typescript-eslint/ban-types: "warn" */
export const ConversationsContextConsumer: React.FC<RenderProps> = props => {
  const context = useConversationsContext();
  return props.children(context);
};

export const ConversationsContextProvider = ConversationsContext.Provider;

export type RenderAnalyticsAttributeAction =
  | 'onReply'
  | 'onResolve'
  | 'onReopen'
  | 'onDelete';

type ConversationContextBuildArgs = Omit<ProviderConfig, 'user'> &
  MentionsBuildArgs & {
    userAvatarUrl: string | undefined;
    userDisplayName: string | undefined;
    userProfileUrl: string | undefined;
    userUuid: string | undefined;
    fabricUser: FabricUser | undefined;
    conversationsMentionContext: ConversationsMentionContext;
    renderAdditionalActions?: boolean;
    eventHandlers?: ConversationEventHandlers;
    supportsCommentResolution?: boolean;
    pullRequest?: PullRequest;
    repoId?: string;
    workspacePaid: boolean;
    highlightedCommentId?: number;
    highlightCommentThread?: boolean;
    baseRevHash?: string;
    editorSource: EditorSource;
    activityFeedVersion?: string;
    renderBetweenCommentAndActions?: (
      comment: FabricComment,
      sendAnalyticsEvent: SendAnalyticsEvent,
      disableRender?: boolean
    ) => JSX.Element;
    additionalAnalyticsAttributes?: (
      comment: FabricComment,
      sentiment: RenderAnalyticsAttributeAction
    ) => object;
  };

export function buildConversationContext({
  anchor,
  destRev,
  userAvatarUrl,
  userDisplayName,
  userProfileUrl,
  userUuid,
  fabricUser,
  urls,
  onAddComment,
  onDeleteComment,
  owner,
  slug,
  intl,
  conversationsMentionContext,
  renderAdditionalActions,
  eventHandlers,
  supportsCommentResolution,
  pullRequest,
  repoId,
  workspacePaid,
  highlightedCommentId,
  highlightCommentThread,
  baseRevHash,
  editorSource,
  activityFeedVersion,
  renderBetweenCommentAndActions,
  additionalAnalyticsAttributes,
}: ConversationContextBuildArgs): ConversationsContextInterface {
  const config: ProviderConfig = {
    anchor,
    destRev,
    // @ts-ignore This is an intentionally incomplete BB.User object
    user: {
      uuid: userUuid || '',
      display_name: userDisplayName || '',
      links: {
        self: { href: userProfileUrl || '#' },
        html: {
          href: userProfileUrl || '#',
        },
        avatar: {
          href: userAvatarUrl || '#',
        },
      },
    },
    urls,
    onAddComment,
    onDeleteComment,
  };

  const conversationProvider = ConversationProvider.getProvider(config);

  // Create and set provider needed for user profile cards
  const profilecardProvider = buildProfilecardProvider({ intl });
  providerFactory.setProvider('profilecardProvider', profilecardProvider);

  // Create and set mention provider needed for @-mentions
  const mentionProvider = createMentionProvider({
    owner,
    slug,
    intl,
    conversationsMentionContext,
  });

  providerFactory.setProvider('mentionProvider', mentionProvider);

  // Create and set context identifier provider needed for @-mentions
  const contextIdentifierProvider = createContextIdentifierProvider({
    conversationsMentionContext,
  });

  providerFactory.setProvider(
    'contextIdentifierProvider',
    contextIdentifierProvider
  );

  return {
    conversationProvider,
    additionalConversationProviders: providerFactory,
    renderAdditionalActions: renderAdditionalActions ?? true,
    eventHandlers,
    supportsCommentResolution,
    user: fabricUser,
    currentUserUuid: userUuid,
    pullRequest,
    workspacePaid,
    repoId,
    highlightedCommentId,
    highlightCommentThread,
    baseRevHash,
    editorSource,
    activityFeedVersion,
    renderBetweenCommentAndActions,
    additionalAnalyticsAttributes,
  };
}
